/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import isFunction from 'lodash.isfunction';

export type Condition = Function | boolean | unknown;
export type ConditionalRenderProps = {
  condition: Condition;
  children: React.ReactNode;
  strict?: boolean
}

/**
 * @deprecated The `ConditionalWrapper` component should no longer be used.
 * For conditional rendering, the {@link Render} component must be used
 * exclusively, which can be imported from `@ion/dls/src/render/Render`
 *
 * The `ConditionalRender` component allows for providing a standardized means
 * of rendering components conditionally.
 *
 * This ensure consistency and simplicity by allowing developers to avoid using
 * different approaches for conditionally rendering components, such as using
 * ternary operators, or using logical AND (&&) operators (which can result in
 * the evaluated boolean value being rendered).
 *
 * ```typescript
 *   import ConditionalRender from '@/dls/src/render/ConditionalRender';
 *   ...
 *
 *   <ConditionalRender condition={truthy | falsy}>
 *     <SomeComponent />
 *   </ConditionalRender>
 *
 * ```
 *
 *
 * ### Rendering ###
 *
 * When the `condition` prop evaluates to `true` (or a truthy value), the
 * provided component (children) will be rendered.
 *
 * ```typescript
 *   const isEnabled = true;
 *   ...
 *
 *   <ConditionalRender condition={isEnabled}>
 *     <SomeComponent />
 *   </ConditionalRender>
 *
 *   // or ...
 *   const isEnabled = () => true;
 *   ...
 *
 *   <ConditionalRender condition={isEnabled}>
 *     <SomeComponent />
 *   </ConditionalRender>
 * ```
 *
 * **Render Bypassing**
 *
 * When the `condition` prop evaluates to `false` (or a falsy value), the
 * provided component (children) will be rendered.
 *
 * ```typescript
 *   const isEnabled = false;
 *   ...
 *   <ConditionalRender condition={isEnabled}>
 *     <SomeComponent />
 *   </ConditionalRender>
 *
 *   // or ...
 *   const isEnabled = () => false;
 *   ...
 *
 *   <ConditionalRender condition={isEnabled}>
 *     <SomeComponent />
 *   </ConditionalRender>
 *
 *   // or ...
 *   const someCondition = '';
 *   ...
 *
 *   <ConditionalRender condition={someCondition}>
 *     <SomeComponent />
 *   </ConditionalRender>
 * ```
 *
 * **Strict Mode Rendering**
 *
 * Strict mode can be used via the (optional) `strict` prop in order to only
 * render when the condition value or condition function return explicitly
 * evaluates to `true`; that is, truthy values aside from explicity boolean
 * `true` will not result in the provided components being rendered. By default,
 * strict mode is set to `false`:
 *
 * ```typescript
 *   <!-- components will render since condition is explicity true -->
 *   const isEnabled = true;
 *   ...
 *   <ConditionalRender condition={isEnabled} strict>
 *    ...
 *   </ConditionalRender>
 *   <ConditionalRender condition={() => true}>
 *    ...
 *   </ConditionalRender>
 *
 *
 *   <!-- components will not render since condition is not explicity true -->
 *   const isEnabled = 'true';
 *   ...
 *   <ConditionalRender condition={isEnabled} strict>
 *    ...
 *   </ConditionalRender>
 *   <ConditionalRender condition={() => 'true'}>
 *    ...
 *   </ConditionalRender>
 * ```
 * @param   condition The condition against which to test, this can be any value
 *          or a function to be executed to determine if the provided component
 *          should be rendered based on the functions return value.
 * @param   children The Component which is to be rendered if the given condition
 *          evaluates to a truthy value.
 * @param   strict When set to `true`, the component will only render if the provided
 *          condition explicitly evaluates to `true`; that is, a non boolean truthy
 *          value will not result in the component being rendered. Defaults to `false`.
 * @returns The provided children if the given condition evaluates to `true`, otherwise
 *          returns `undefined`.
 */
export const ConditionalRender = (
  {condition, children, strict = false}: ConditionalRenderProps): React.ReactElement | undefined => {
  const test = (isFunction(condition) ? (condition as Function)() : condition);
  const isValid = strict ? test === true : !!test;
  if (isValid) {
    return children as unknown as React.ReactElement;
  }
  return undefined;
};

export default ConditionalRender;
